/**
 * LiveStreamPlayer — THE single live video player for the cloud frontend.
 *
 * Used by CamerasPage, LiveView, MultiSiteView, and StreamGrid.
 * Handles:
 *   - Attaching MediaStream to <video>
 *   - Health monitoring (frame stall + slow network detection)
 *   - Simple loader / error overlays with exact messages
 *   - IntersectionObserver for lazy rendering
 *   - onStalled callback for parent reconnect logic
 *
 * The PARENT is responsible for:
 *   - Signaling, WebRTC, reconnect logic
 *   - Passing the stream + connectionState to this component
 *
 * Display states and their messages:
 *   LOADERS (white spinner + message):
 *     connecting        → "connecting..."
 *     signaling         → "signaling..."
 *     waiting_stream    → "waiting for stream..."
 *     buffering         → "waiting for stream..."  (stream attached, first frame not decoded)
 *     reconnecting      → "reconnecting..."
 *
 *   ERRORS (message only, no icons):
 *     node_unreachable  → "site not reachable"
 *     signaling_error   → "signaling error"
 *     camera_unreachable→ "camera unreachable"
 *     network_error     → "network error"
 *     slow_network      → "slow network"
 *     stalled           → "camera unreachable"  (frames stopped = pipeline/camera dead)
 */
import React, { useRef, useEffect, useState, memo, useCallback } from 'react';
import useMediaHealthMonitor from '../hooks/useMediaHealthMonitor';
import { Loader, RefreshCw, Lock } from 'lucide-react';
import trinetraLogoWhite from '../assets/logos/trinetra-logo-white.png';

/** Normalised connection state understood by this component */
export type LiveConnectionState =
  | 'new'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'failed'
  | 'pending'
  | 'signaling'
  | 'waiting_stream'
  | 'rtc_established'
  | 'stalled'
  | 'reconnecting'
  | 'node_unreachable'
  | 'signaling_error'
  | 'camera_unreachable'
  | 'network_error'
  | 'slow_network';

export interface LiveStreamPlayerProps {
  stream: MediaStream | null;
  connectionState?: LiveConnectionState | string;
  label?: string;
  subLabel?: string;
  muted?: boolean;
  fillContainer?: boolean;
  onStalled?: () => void;
  onPlaying?: () => void;
  onRetry?: () => void;
  /** Layer drawn over the video frame (e.g. heatmap), aligned to its content box. */
  videoOverlay?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export default memo(function LiveStreamPlayer({
  stream,
  connectionState = 'new',
  label,
  subLabel,
  muted = true,
  fillContainer = false,
  onStalled,
  onPlaying,
  onRetry,
  videoOverlay,
  children,
  className,
}: LiveStreamPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  const [contentBox, setContentBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  // Reset first-frame flag when stream changes
  useEffect(() => {
    setHasFirstFrame(false);
    const video = videoRef.current;
    if (!video || !stream) return;
    if (video.readyState >= 2) { setHasFirstFrame(true); return; }
    const onFrame = () => setHasFirstFrame(true);
    video.addEventListener('loadeddata', onFrame);
    video.addEventListener('playing', onFrame);
    return () => {
      video.removeEventListener('loadeddata', onFrame);
      video.removeEventListener('playing', onFrame);
    };
  }, [stream]);

  // Health monitor
  const isActive = !!stream && visible;
  const { status: healthStatus, resetMonitor } = useMediaHealthMonitor(videoRef, isActive);

  useEffect(() => {
    if (stream) resetMonitor();
  }, [stream, resetMonitor]);

  // Notify parent on stall (once — SFU auto-recovers when camera comes back)
  // Only fire for 'stalled' (no frames at all), NOT 'slow_network' (frames still flowing)
  useEffect(() => {
    if (healthStatus !== 'stalled' || !onStalled) return;
    if (connectionState === 'node_unreachable' || connectionState === 'network_error' || connectionState === 'slow_network') return;
    onStalled();
  }, [healthStatus, onStalled, connectionState]);

  // Attach stream (clear srcObject when stream is removed to prevent stale black frames)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
  }, [stream]);

  // Intersection observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ── Video content box ──
  // object-contain letterboxes the frame inside the container, so an overlay
  // that must line up with the picture (the heatmap) needs the real content
  // box, not the container box.
  useEffect(() => {
    if (!videoOverlay) return;
    const video = videoRef.current;
    const el = containerRef.current;
    if (!video || !el) return;

    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      let box: { left: number; top: number; width: number; height: number };
      if (fillContainer || !vw || !vh) {
        box = { left: 0, top: 0, width: cw, height: ch };
      } else {
        const scale = Math.min(cw / vw, ch / vh);
        const w = vw * scale;
        const h = vh * scale;
        box = { left: (cw - w) / 2, top: (ch - h) / 2, width: w, height: h };
      }
      setContentBox((prev) => (
        prev && prev.left === box.left && prev.top === box.top &&
        prev.width === box.width && prev.height === box.height ? prev : box
      ));
    };

    measure();
    video.addEventListener('loadedmetadata', measure);
    video.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      video.removeEventListener('loadedmetadata', measure);
      video.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [videoOverlay, fillContainer, stream]);

  const display = deriveDisplayState(stream, healthStatus, connectionState, hasFirstFrame);
  const isPlaying = display === 'playing';

  // Notify parent when camera starts playing (used to clear stall recovery state)
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    if (isPlaying && !prevPlayingRef.current && onPlaying) {
      onPlaying();
    }
    prevPlayingRef.current = isPlaying;
  }, [isPlaying, onPlaying]);
  const overlay = OVERLAY_MAP[display];
  const isError = !isPlaying && overlay?.type === 'error';
  const isAccessDenied = display === 'access_denied';
  // Show retry for all error states EXCEPT access_denied — an RBAC denial is
  // terminal, so no countdown/retry (that's what caused the endless reconnect).
  const needsRetry = isError && !isAccessDenied;

  // Progressive retry: 3s → 5s → 10s → 15s → 15s...
  // retryAttempt persists across needsRetry toggles so rapid state transitions
  // (camera_unreachable → pending → waiting_stream → camera_unreachable) don't
  // reset to the fast 3s tier every cycle.
  const RETRY_TIERS = [3, 5, 10, 15];
  const retryAttemptRef = useRef(0);
  const [retryCountdown, setRetryCountdown] = useState(RETRY_TIERS[0]);
  const retryCooldownRef = useRef(false);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  // Only reset retry tier on successful playback (not on every state toggle)
  useEffect(() => {
    if (isPlaying) {
      retryAttemptRef.current = 0;
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!needsRetry) {
      // Don't reset retryAttemptRef here — preserve backoff across fast state transitions
      return;
    }
    const tierIdx = Math.min(retryAttemptRef.current, RETRY_TIERS.length - 1);
    const tierSecs = RETRY_TIERS[tierIdx];
    setRetryCountdown(tierSecs);
    const iv = setInterval(() => {
      setRetryCountdown(prev => {
        if (prev <= 1) {
          retryAttemptRef.current++;
          const nextIdx = Math.min(retryAttemptRef.current, RETRY_TIERS.length - 1);
          setTimeout(() => { onRetryRef.current?.(); }, 0);
          return RETRY_TIERS[nextIdx];
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [needsRetry]);

  const handleManualRetry = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (retryCooldownRef.current) return;
    retryCooldownRef.current = true;
    setTimeout(() => { retryCooldownRef.current = false; }, 5000);
    retryAttemptRef.current = 0;
    setRetryCountdown(RETRY_TIERS[0]);
    if (onRetry) onRetry();
  }, [onRetry]);

  // Status dot: green=playing, amber=loading, red=error
  const dotColor = isPlaying ? 'bg-emerald-500' : overlay?.type === 'loader' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div
      ref={containerRef}
      className={`relative bg-black rounded-lg overflow-hidden ${className ?? 'aspect-video'}`}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        className={`absolute inset-0 w-full h-full ${fillContainer ? 'object-cover' : 'object-contain'}`}
        style={{ display: isPlaying ? 'block' : 'none' }}
      />

      {/* Caller-supplied layer (heatmap, zones, …), aligned to the video frame */}
      {videoOverlay && (
        <div
          className={`absolute pointer-events-none ${contentBox ? '' : 'inset-0'}`}
          style={contentBox ? { ...contentBox, zIndex: 4 } : { zIndex: 4 }}
        >
          {videoOverlay}
        </div>
      )}

      {/* Overlay */}
      {!isPlaying && overlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          {/* h-full so the brand mark's percentage caps resolve against the PLAYER — this
              overlay is inset-0, so its height is definite where a shrink-to-fit column's is
              not. justify-center keeps the stack centred exactly as before. */}
          <div className="flex h-full w-full flex-col items-center justify-center gap-2.5">
            {/* Brand mark above every status message — a tile that is connecting, waiting or
                unreachable reads as ours rather than as a broken black rectangle.
                The local dashboard's player draws the same mark the same way.
                Capped as a fraction of the PLAYER in both axes with no explicit width or height,
                so the browser keeps the aspect ratio and the mark scales itself from ~290px wide
                on a full-page view down to ~40px in an 8×8 wall tile — it can never grow tall
                enough to push the message out of a short tile. `alt=""` + aria-hidden because
                the message below is what carries the meaning. */}
            <img
              src={trinetraLogoWhite}
              alt=""
              aria-hidden="true"
              draggable={false}
              className="max-h-[20%] max-w-[42%] select-none opacity-90"
            />
            {overlay.type === 'loader' && (
              <Loader className="h-5 w-5 text-white/70 animate-spin" />
            )}
            {isAccessDenied && (
              <Lock className="h-5 w-5 text-white/60" />
            )}
            <span className="text-white/60 text-xs">{overlay.message}</span>
            {needsRetry && onRetry && (
              <>
                <span className="text-white/40 text-[10px]">retrying in {retryCountdown}s</span>
                <button
                  onClick={handleManualRetry}
                  className="flex items-center gap-1 px-2 py-0.5 bg-white/10 hover:bg-white/20 text-white/60 text-[10px] rounded transition-colors"
                >
                  <RefreshCw className="h-2.5 w-2.5" /> retry now
                </button>
              </>
            )}
            {(display === 'camera_unreachable' || display === 'stalled') && !needsRetry && (
              <span className="text-white/30 text-[10px]">will recover automatically when camera is back</span>
            )}
          </div>
        </div>
      )}

      {/* Slow network indicator — shown ON TOP of playing video, not as error overlay */}
      {isPlaying && healthStatus === 'slow_network' && (
        <div className="absolute top-1.5 left-1.5 z-20 px-1.5 py-0.5 rounded bg-amber-600/80 text-white text-[10px] font-medium">
          slow network
        </div>
      )}

      {/* Bottom bar */}
      {(label || children) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
            {label && <span className="text-white text-[11px] font-medium truncate">{label}</span>}
            {subLabel && <span className="text-white/50 text-[10px] truncate">· {subLabel}</span>}
          </div>
          {children}
        </div>
      )}
    </div>
  );
});

// ─── Display state ───────────────────────────────────────────────
type DisplayState =
  | 'playing'
  | 'connecting'
  | 'signaling'
  | 'waiting_stream'
  | 'buffering'
  | 'reconnecting'
  | 'node_unreachable'
  | 'signaling_error'
  | 'camera_unreachable'
  | 'network_error'
  | 'slow_network'
  | 'stalled'
  | 'access_denied'
  | 'no_stream';

function deriveDisplayState(
  stream: MediaStream | null,
  healthStatus: string,
  connectionState: string,
  hasFirstFrame: boolean,
): DisplayState {
  // Stream present — check health first
  if (stream) {
    if (healthStatus === 'stalled') return 'stalled';
    if (healthStatus === 'slow_network') return 'playing'; // keep video visible — stream IS decoding
    if (healthStatus === 'healthy') return 'playing';
    if (healthStatus === 'waiting') {
      if (hasFirstFrame) return 'playing';
      // No first frame yet — if connectionState indicates the camera is
      // known-bad (reconnecting/camera_unreachable/stalled), show that
      // instead of generic "waiting for stream..." so dead cameras don't
      // mislead the user during resubscribe cycles.
      const s = connectionState;
      if (s === 'reconnecting' || s === 'stalled') return 'reconnecting';
      if (s === 'camera_unreachable') return 'camera_unreachable';
      return 'buffering';
    }
  }

  // Map connection states
  const s = connectionState;
  if (s === 'access_denied') return 'access_denied'; // terminal — checked before any fallback
  if (s === 'connected' || s === 'rtc_established') return stream ? 'playing' : 'waiting_stream';
  if (s === 'waiting_stream') return 'waiting_stream';
  if (s === 'connecting' || s === 'new' || s === 'pending') return 'connecting';
  if (s === 'signaling') return 'signaling';
  if (s === 'reconnecting') return 'reconnecting';
  if (s === 'stalled') return 'reconnecting';
  if (s === 'node_unreachable') return 'node_unreachable';
  if (s === 'signaling_error') return 'signaling_error';
  if (s === 'camera_unreachable') return 'camera_unreachable';
  if (s === 'network_error') return 'network_error';
  if (s === 'slow_network') return 'slow_network';
  if (s === 'disconnected' || s === 'failed') return 'node_unreachable';

  return stream ? 'playing' : 'no_stream';
}

// ─── Overlay definitions ─────────────────────────────────────────
interface OverlayDef {
  type: 'loader' | 'error';
  message: string;
}

const OVERLAY_MAP: Record<string, OverlayDef | undefined> = {
  connecting:        { type: 'loader', message: 'connecting...' },
  signaling:         { type: 'loader', message: 'signaling...' },
  waiting_stream:    { type: 'loader', message: 'waiting for stream...' },
  buffering:         { type: 'loader', message: 'waiting for stream...' },
  reconnecting:      { type: 'loader', message: 'reconnecting...' },
  node_unreachable:  { type: 'error',  message: 'site not reachable' },
  signaling_error:   { type: 'error',  message: 'signaling error' },
  camera_unreachable:{ type: 'error',  message: 'camera unreachable' },
  network_error:     { type: 'error',  message: 'network error' },
  slow_network:      { type: 'error',  message: 'slow network' },
  stalled:           { type: 'error',  message: 'camera unreachable' },
  access_denied:     { type: 'error',  message: 'access denied' },
  no_stream:         { type: 'loader', message: 'connecting...' },
  playing:           undefined,
};
